import React, { useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Box, Drawer, Grid, Typography } from '@mui/material';
import './AddLoadConfig.scss';
import CustomTextField from '../../../../common/components/customized/customtextfield/CustomTextField';
import CustomButton from '../../../../common/components/buttons/CustomButton';
import { useAppDispatch } from '../../../../app/redux/hooks';
import {
  addLoadConfig,
  getLoadsConfigAction,
  updateLoadConfig
} from '../../redux/reducer/deliveryTrackingSlices/loadConfigSlice';

interface LoadInfo {
  id?: number;
  width: number | null;
  depth: number | null;
  height: number | null;
  unit: string;
  orderQuantity: string;
  weight: string;
}

interface LoadFormValues {
  loadList?: Array<{
    width?: number | null;
    depth?: number | null;
    height?: number | null;
    orderQuantity: string;
    weight: string;
  }>;
}

interface AddLoadConfigProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selected?: LoadInfo | null;
  loading: boolean;
}

const schema = yup.object().shape({
  loadList: yup.array().of(
    yup.object().shape({
      width: yup
        .number()
        .nullable()
        .typeError('Width must be a number')
        .test('required', 'Width is required', value => value !== null)
        .moreThan(0, 'Enter valid width (must be greater than 0)')
        .test('no-leading-zero', 'Width should not start with a leading zero', value => {
          if (value === undefined || value === null) return false;
          const strVal = String(value);
          return !/^0[0-9]+$/.test(strVal);
        }),

      depth: yup
        .number()
        .nullable()
        .typeError('Depth must be a number')
        .test('required', 'Depth is required', value => value !== null)
        .moreThan(0, 'Enter valid depth (must be greater than 0)')
        .test('no-leading-zero', 'Depth should not start with a leading zero', value => {
          if (value === undefined || value === null) return false;
          const strVal = String(value);
          return !/^0[0-9]+$/.test(strVal);
        }),

      height: yup
        .number()
        .nullable()
        .typeError('Height must be a number')
        .test('required', 'Height is required', value => value !== null)
        .moreThan(0, 'Enter valid height (must be greater than 0)')
        .test('no-leading-zero', 'Height should not start with a leading zero', value => {
          if (value === undefined || value === null) return false;
          const strVal = String(value);
          return !/^0[0-9]+$/.test(strVal);
        }),

      orderQuantity: yup
        .string()
        .required('Order quantity is required'),

      weight: yup
        .string()
        .required('Weight is required')
        .matches(
          /^\d+(\.\d{1,2})?$/,
          'Weight must be a valid number (e.g., 100 or 100.5)'
        )
    })
  )
});

const AddLoadConfig: React.FC<AddLoadConfigProps> = ({
  isOpen,
  setIsOpen,
  selected,
  loading
}) => {
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    reset
  } = useForm<LoadFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      loadList: [
        {
          width: null,
          depth: null,
          height: null,
          orderQuantity: '',
          weight: ''
        }
      ]
    }
  });

  const { fields: loadFields } = useFieldArray({
    control,
    name: 'loadList'
  });

  useEffect(() => {
    if (selected) {
      reset({
        loadList: [
          {
            width: selected.width ?? null,
            depth: selected.depth ?? null,
            height: selected.height ?? null,
            orderQuantity: selected.orderQuantity || '',
            weight: selected.weight || ''
          }
        ]
      });
    } else {
      reset({
        loadList: [
          {
            width: null,
            depth: null,
            height: null,
            orderQuantity: '',
            weight: ''
          }
        ]
      });
    }
  }, [selected, reset]);

  const handleFormSubmit = async (data: any) => {
    const loadConfigData = {
      quantityLimit: parseInt(data.loadList[0].orderQuantity) || 1,
      radiusLimit: 150,
      width: parseFloat(data.loadList[0].width),
      depth: parseFloat(data.loadList[0].depth),
      height: parseFloat(data.loadList[0].height),
      weight: parseFloat(data.loadList[0].weight),
      unit: 'mm'
    };
    if (selected) {
      const response = await dispatch(
        updateLoadConfig({
          id: Number(selected?.id),
          updatePayload: loadConfigData,
          pageNo: 1,
          pageSize: 500
        })
      );
      if (updateLoadConfig.fulfilled.match(response)) {
        await dispatch(getLoadsConfigAction({ pageNo: 1, pageSize: 12 }));
        reset({
          loadList: [
            {
              width: null,
              depth: null,
              height: null,
              orderQuantity: '',
              weight: ''
            }
          ]
        });
        handleClose();
      }
    } else {
      const response = await dispatch(
        addLoadConfig({ data: loadConfigData, pageNo: 1, pageSize: 500 })
      );
      if (updateLoadConfig.fulfilled.match(response)) {
        await dispatch(getLoadsConfigAction({ pageNo: 1, pageSize: 12 }));

        reset({
          loadList: [
            {
              width: null,
              depth: null,
              height: null,
              orderQuantity: '',
              weight: ''
            }
          ]
        });
        handleClose();
      }
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    reset();
  };

  if (!isOpen) return null;

  return (
    <Drawer open={isOpen} anchor='right' sx={{ zIndex: 1 }}>
      <Box
        component='form'
        onSubmit={handleSubmit(handleFormSubmit)}
        className='add-load-container'
      >
        <Box className='add-load-header'>
          <Typography variant='h6' className='add-load-title'>
            {selected ? 'Update Load' : 'Add Load'}
          </Typography>
        </Box>

        {loadFields.map((field, index) => (
          <Grid container spacing={2} key={field.id}>
            <Grid item xs={12}>
              <CustomTextField
                id={`width-${index}`}
                control={control}
                name={`loadList.${index}.width`}
                label='Width (mm)'
                placeholder='Enter Width (mm)'
                type='number'
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                id={`depth-${index}`}
                control={control}
                name={`loadList.${index}.depth`}
                label='Depth (mm)'
                placeholder='Enter Depth (mm)'
                type='number'
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                id={`height-${index}`}
                control={control}
                name={`loadList.${index}.height`}
                label='Height (mm)'
                placeholder='Enter Height (mm)'
                type='number'
              />
            </Grid>
            <Grid item lg={12} md={12} sm={12}>
              <CustomTextField
                id={`orderQuantity-${index}`}
                control={control}
                name={`loadList.${index}.orderQuantity`}
                label='Order Quantity'
                placeholder='Enter Quantity'
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                id={`weight-${index}`}
                control={control}
                name={`loadList.${index}.weight`}
                label='Weight (kg)'
                placeholder='Enter Weight (Kg)'
                type='number'
              />
            </Grid>
          </Grid>
        ))}
        <Box className='action-buttons'>
          <CustomButton category='Cancel' className='cancel' onClick={handleClose} />
          <CustomButton
            category={selected ? 'Update' : 'Add'}
            type='submit'
            className='saveChanges'
            loading={loading}
          />
        </Box>
      </Box>
    </Drawer>
  );
};

export default AddLoadConfig;
